
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, getUnreadNotificationCount, deleteAllNotifications } from '@/utils/social/notifications';
import type { Notification, NotificationType } from '@/utils/social/notifications';

// Smart grouping interface
interface GroupedNotification extends Notification {
  isGroup: boolean;
  groupCount: number;
  groupedUsers: Array<{
    id: string;
    display_name: string;
    avatar_url: string | null;
  }>;
  latestActivity: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [groupedNotifications, setGroupedNotifications] = useState<GroupedNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  
  const PAGE_SIZE = 20; // Smaller page size for better performance

  // Simple grouping - just convert notifications to the grouped format without complex bundling
  const groupNotifications = (notificationList: Notification[]): GroupedNotification[] => {
    return notificationList.map(notification => ({
      ...notification,
      isGroup: false,
      groupCount: 1,
      groupedUsers: notification.user ? [{
        id: notification.user.id,
        display_name: notification.user.display_name,
        avatar_url: notification.user.avatar_url
      }] : [],
      latestActivity: notification.created_at
    }));
  };

  const fetchNotifications = async (reset = true) => {
    if (!user) {
      setLoading(false);
      return;
    }

    if (reset) {
      setLoading(true);
      setCurrentPage(0);
      setError(null);
    }

    try {
      const [notificationsData, unreadCountData] = await Promise.all([
        getNotifications(PAGE_SIZE),
        getUnreadNotificationCount()
      ]);

      if (reset) {
        setNotifications(notificationsData);
        setGroupedNotifications(groupNotifications(notificationsData));
        setHasMore(notificationsData.length === PAGE_SIZE);
      } else {
        const allNotifications = [...notifications, ...notificationsData];
        setNotifications(allNotifications);
        setGroupedNotifications(groupNotifications(allNotifications));
        setHasMore(notificationsData.length === PAGE_SIZE);
      }
      
      setUnreadCount(unreadCountData);
      setRetryCount(0); // Reset retry count on success
      setError(null);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch notifications');
      
      // Implement exponential backoff for retries
      if (retryCount < 3 && navigator.onLine) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchNotifications(reset);
        }, delay);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setIsRetrying(false);
    }
  };

  const loadMoreNotifications = async () => {
    if (!user || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const notificationsData = await getNotifications(PAGE_SIZE, nextPage * PAGE_SIZE);
      
      if (notificationsData.length > 0) {
        const allNotifications = [...notifications, ...notificationsData];
        setNotifications(allNotifications);
        setGroupedNotifications(groupNotifications(allNotifications));
        setCurrentPage(nextPage);
        setHasMore(notificationsData.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  // On mobile real-time is disabled, so refetch whenever the page/tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        fetchNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  // Refresh immediately when a push arrives while the app is in foreground
  useEffect(() => {
    const handlePushReceived = () => {
      if (user) fetchNotifications();
    };
    window.addEventListener('push-notification-received', handlePushReceived);
    return () => window.removeEventListener('push-notification-received', handlePushReceived);
  }, [user]);

  // Set up real-time subscription for new notifications
  useEffect(() => {
    // MOBILE FIX: Disable real-time on mobile to prevent 13 MB WebSocket leak
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      console.log('📱 [useNotifications] Skipping subscription on mobile');
      return undefined;
    }
    
    if (!user) return undefined; // No subscription needed if no user

    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('New notification:', payload);
          fetchNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Notification updated:', payload);
          fetchNotifications();
        }
      )
      .subscribe();

    // ✅ FIX: Cleanup runs when user changes to null OR component unmounts
    return () => {
      console.log('🧹 [useNotifications] Cleaning up subscription for user:', user?.id);
      supabase.removeChannel(channel);
      console.log('✅ [useNotifications] Cleanup complete - channel removed');
    };
  }, [user]);


  const markAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      
      // Optimistically update UI immediately
      const updatedNotifications = notifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      );
      setNotifications(updatedNotifications);
      setGroupedNotifications(groupNotifications(updatedNotifications));
      setUnreadCount(prev => Math.max(0, prev - 1));
      setError(null);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setError('Failed to mark notification as read. It will be retried when connection is restored.');
      
      // If offline, the operation was queued, so show optimistic update anyway
      if (!navigator.onLine) {
        const updatedNotifications = notifications.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        );
        setNotifications(updatedNotifications);
        setGroupedNotifications(groupNotifications(updatedNotifications));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      await markAllNotificationsAsRead();

      // Optimistically update UI immediately
      const updatedNotifications = notifications.map(notification => ({ ...notification, read: true }));
      setNotifications(updatedNotifications);
      setGroupedNotifications(groupNotifications(updatedNotifications));
      setUnreadCount(0);
      setError(null);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setError('Failed to mark all notifications as read. It will be retried when connection is restored.');

      // If offline, the operation was queued, so show optimistic update anyway
      if (!navigator.onLine) {
        const updatedNotifications = notifications.map(notification => ({ ...notification, read: true }));
        setNotifications(updatedNotifications);
        setGroupedNotifications(groupNotifications(updatedNotifications));
        setUnreadCount(0);
      }
    }
  };

  const clearAll = async () => {
    if (!user) return;
    try {
      await deleteAllNotifications();
      setNotifications([]);
      setGroupedNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const removeNotification = (notificationId: string) => {
    const updated = notifications.filter(n => n.id !== notificationId);
    setNotifications(updated);
    setGroupedNotifications(groupNotifications(updated));
    setUnreadCount(prev => {
      const wasUnread = notifications.find(n => n.id === notificationId && !n.read);
      return wasUnread ? Math.max(0, prev - 1) : prev;
    });
  };

  // Marks all read in DB and resets counter. Does NOT touch local read flags
  // so green highlights remain visible during this session. On remount the
  // fresh fetch will return read:true from DB, so greens won't reappear.
  const resetUnreadCount = async () => {
    if (!user) return;
    try {
      await markAllNotificationsAsRead();
      setUnreadCount(0);
    } catch (error) {
      console.error('Error resetting unread count:', error);
    }
  };


  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus(true);
      setError(null);
      
      // Retry failed operations when back online
      if (error && retryCount > 0) {
        setIsRetrying(true);
        fetchNotifications();
      }
    };
    
    const handleOffline = () => {
      setNetworkStatus(false);
      setError('You are offline. Changes will be synced when connection is restored.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [error, retryCount]);


  // Manual retry function
  const retryFailedOperations = useCallback(async () => {
    if (!error || isRetrying) return;
    
    setIsRetrying(true);
    setError(null);
    
    try {
      // Import and check offline queue
      const { offlineNotificationQueue } = await import('../services/offline-notification-queue.service');
      await offlineNotificationQueue.retryFailedItems();
      
      // Refresh notifications
      await fetchNotifications();
    } catch (retryError) {
      console.error('Failed to retry operations:', retryError);
      setError('Retry failed. Please try again later.');
    } finally {
      setIsRetrying(false);
    }
  }, [error, isRetrying]);

  // Get offline queue metrics
  const getOfflineQueueStatus = useCallback(async () => {
    try {
      const { offlineNotificationQueue } = await import('../services/offline-notification-queue.service');
      return offlineNotificationQueue.getQueueMetrics();
    } catch {
      return null;
    }
  }, []);

  return {
    notifications: groupedNotifications,
    rawNotifications: notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    markAsRead,
    markAllAsRead,
    resetUnreadCount,
    clearAll,
    removeNotification,
    refetch: fetchNotifications,
    loadMore: loadMoreNotifications,
    // Enhanced error handling and retry features
    error,
    networkStatus,
    isRetrying,
    retryCount,
    retryFailedOperations,
    getOfflineQueueStatus
  };
}
